import {
  type OrderRequestRow,
  type Pricing,
  type RegisterOrderRequestInput,
  calculatePricing,
} from '#shared/types';
import type { BillingValues, DemographicValues } from '#client/features/patients';

type RequestLine = OrderRequestRow['lines'][number];
type MatchedTest = NonNullable<RequestLine['lab_test']>;

/** A requested line that has been matched to a catalogue test, so can be booked. */
export interface BookableLine {
  lineId: number;
  requestedText: string;
  test: MatchedTest;
}

/** The counter's demographic and billing fields, plus what the request adds. */
export type RegisterValues = DemographicValues &
  BillingValues & {
    whatsapp_consent: boolean;
    note: string;
  };

/** The lines that can be booked: only those matched to a catalogue entry. */
export function listBookableLines(lines: readonly RequestLine[]): BookableLine[] {
  return lines.flatMap((line) =>
    line.lab_test
      ? [{ lineId: line.id, requestedText: line.requested_text, test: line.lab_test }]
      : [],
  );
}

export function countUnmatchedLines(lines: readonly RequestLine[]): number {
  return lines.filter((line) => line.test_id === null).length;
}

/** What the request already says, over the defaults the desk starts from. */
export function initialRegisterValues(request: OrderRequestRow): RegisterValues {
  return {
    age_year: request.age_year ?? 0,
    age_month: 0,
    age_days: 0,
    gender: request.gender ?? 'other',
    married_status: 'single',
    phone_number: request.phone_number ?? '',
    cnic_number: '',
    email: '',
    refer_by: request.referred_by ?? '',
    discount_rupees: 0,
    discount_percentage: 0,
    tax_percentage: 0,
    other_charges: 0,
    other_charges_label: '',
    payment_amount: 0,
    payment_method: null,
    whatsapp_consent: false,
    note: request.note ?? '',
  };
}

/**
 * The same function the server bills with, so what the receptionist reads to
 * the patient and what is stored cannot disagree. The server recomputes it
 * regardless — this is a preview, not the source of truth.
 */
export function previewRegisterPricing(
  values: RegisterValues,
  bookable: readonly BookableLine[],
  chosenTestIds: ReadonlySet<number>,
): Pricing {
  const prices = bookable
    .filter((line) => chosenTestIds.has(line.test.id))
    .map((line) => line.test.price);
  return calculatePricing(
    {
      discount_rupees: values.discount_rupees,
      discount_percentage: values.discount_percentage,
      tax_percentage: values.tax_percentage,
      other_charges: values.other_charges,
      paid: values.payment_amount,
    },
    prices,
  );
}

/** Blank text is sent as null; a method is only sent alongside money received. */
export function buildRegisterBody(
  values: RegisterValues,
  chosenTestIds: ReadonlySet<number>,
): RegisterOrderRequestInput {
  return {
    test_ids: [...chosenTestIds],
    panel_ids: [],
    age_year: values.age_year,
    age_month: values.age_month,
    age_days: values.age_days,
    gender: values.gender,
    married_status: values.married_status,
    phone_number: values.phone_number || null,
    cnic_number: values.cnic_number || null,
    email: values.email || null,
    refer_by: values.refer_by || null,
    discount_rupees: values.discount_rupees,
    discount_percentage: values.discount_percentage,
    tax_percentage: values.tax_percentage,
    other_charges: values.other_charges,
    other_charges_label: values.other_charges_label || null,
    payment_amount: values.payment_amount,
    payment_method: values.payment_amount > 0 ? (values.payment_method ?? null) : null,
    whatsapp_consent: values.whatsapp_consent,
    note: values.note || null,
  };
}

export function toggleTestId(current: ReadonlySet<number>, testId: number, isChosen: boolean) {
  const next = new Set(current);
  if (isChosen) next.add(testId);
  else next.delete(testId);
  return next;
}
